import { formatAuditAction as formatAction } from '@/lib/auditDisplay';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { Building2, Users, Truck, Activity, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminUnits, getAdminUsers, getAuditLogs, getGlobalAuditLogs } from '@/lib/adminApi';
import { MODEL_LABELS, MODEL_TYPE_MAPPING } from '@/lib/constants';
import { formatAuditTimestamp } from '@/lib/utils';
import type { AuditLog, IndustryType } from '@/types';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: string;
  trendUp?: boolean;
  loading?: boolean;
}

function StatCard({ title, value, icon: Icon, trend, trendUp, loading }: StatCardProps) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 relative overflow-hidden group hover:border-border transition-colors">
      <div className="absolute right-0 top-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
        <Icon className="w-16 h-16 text-[#8FB560]" />
      </div>
      <div className="relative z-10">
        <p className="text-sm text-muted-foreground font-medium">{title}</p>
        <div className="flex items-end gap-2 mt-2">
          {loading ? (
            <div className="h-9 w-24 bg-muted animate-pulse rounded" />
          ) : (
            <h3 className="text-3xl font-bold text-foreground">{value}</h3>
          )}
          {trend && !loading && (
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${trendUp ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
              {trend}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders the settings dashboard with organization stats, unit breakdown, recent activity, invoices, notifications, and profile/quick links.
 *
 * Conditionally hides non-superadmin content when the app is running in demo mode.
 *
 * @returns The settings overview UI as a JSX element.
 */
export function SettingsOverview() {
  const { user, companies, is_superadmin } = useSettingsContext();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    unitsCount: 0,
    usersCount: 0,
    systemStatus: 'Checking...',
    loading: true
  });

  const [modelBreakdown, setModelBreakdown] = useState<{ model: string; count: number; percentage: number }[]>([]);

  // Determine allowed industry types for industry-restricted companies
  // Superadmins and internal companies see all industries
  // Companies with specific industry see only that industry's models
  const allowedIndustryTypes = useMemo((): IndustryType[] | null => {
    if (is_superadmin) return null; // null = show all
    // If any company has a specific industry, filter to that
    const companyWithIndustry = companies.find(a => a.industry);
    if (companyWithIndustry) {
      return [companyWithIndustry.industry as IndustryType];
    }
    return null; // No restriction
  }, [is_superadmin, companies]);
  
  const [data, setData] = useState<{
    logs: AuditLog[];
    loading: boolean;
  }>({
    logs: [],
    loading: true
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch stats data in parallel (use admin API for test mode support)
        const [unitsResponse, usersResponse] = await Promise.allSettled([
          getAdminUnits(),
          getAdminUsers()
        ]);

        const units = unitsResponse.status === 'fulfilled' ? unitsResponse.value.units : [];
        const unitsCount = units.length;
        const usersCount = usersResponse.status === 'fulfilled' ? (usersResponse.value.users?.length || 0) : 0;

        // Determine system status based on successful fetches
        const isHealthy = unitsResponse.status === 'fulfilled' && usersResponse.status === 'fulfilled';

        setStats({
          unitsCount,
          usersCount,
          systemStatus: isHealthy ? '99.9%' : 'Degraded',
          loading: false
        });

        // Calculate model breakdown if units data is available
        if (units.length > 0) {
          const modelCounts: Record<string, number> = {};

          // Extract model code from unit_code (first 3 characters)
          units.forEach((unit) => {
            const modelCode = unit.unit_code?.substring(0, 3) || 'UNK';
            modelCounts[modelCode] = (modelCounts[modelCode] || 0) + 1;
          });

          // Filter by allowed industry types if user has industry restrictions
          // This ensures users only see models belonging to their industry
          // Also filter out unknown model codes (not in MODEL_LABELS)
          const filteredModelCounts = Object.entries(modelCounts).filter(([modelCode]) => {
            // Skip unknown model codes (not in our defined labels)
            if (!MODEL_LABELS[modelCode]) return false;

            // If no industry type restriction, show all known models
            if (!allowedIndustryTypes) return true;

            // Check if this model belongs to any of the allowed industry types
            const modelIndustryTypes = MODEL_TYPE_MAPPING[modelCode] || [];
            return modelIndustryTypes.some(industryType => allowedIndustryTypes.includes(industryType));
          });

          // Calculate total count after filtering for accurate percentages
          const filteredTotal = filteredModelCounts.reduce((sum, [, count]) => sum + count, 0);

          // Convert to array and calculate percentages
          // Map model codes to human-readable names using MODEL_LABELS
          const breakdown = filteredModelCounts
            .map(([modelCode, count]) => ({
              model: MODEL_LABELS[modelCode] || modelCode, // Use MODEL_LABELS for display
              count,
              percentage: filteredTotal > 0 ? Math.round((count / filteredTotal) * 100) : 0
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6); // Top 6 models

          setModelBreakdown(breakdown);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        setStats(prev => ({ ...prev, loading: false, systemStatus: 'Error' }));
      }
    };

    const fetchContentData = async () => {
      try {
        // Use global audit logs for superadmins, regular audit logs otherwise
        const logsResponse = is_superadmin ? await getGlobalAuditLogs() : await getAuditLogs();

        setData({
          logs: logsResponse.logs,
          loading: false
        });
      } catch (error) {
        console.error('Failed to fetch dashboard content:', error);
        setData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchDashboardData();
    fetchContentData();
  }, [allowedIndustryTypes, is_superadmin]);

  const isLoading = stats.loading || data.loading;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 flex-shrink-0">
        <div className="flex-shrink-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground whitespace-nowrap">Dashboard</h1>
          <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
            Overview of your organization and activity.
          </p>
        </div>
        <Button className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap w-full sm:w-auto" onClick={() => navigate('/settings/audit')}>
          <Activity className="w-4 h-4 mr-2" />
          View Reports
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 flex-shrink-0">
        <StatCard
          title="Total Companies"
          value={companies.length}
          icon={Building2}
          trend="+2"
          trendUp={true}
        />
        <StatCard
          title="Active Members"
          value={stats.usersCount}
          icon={Users}
          trend="+5%"
          trendUp={true}
          loading={stats.loading}
        />
        <StatCard
          title="Unit Count"
          value={stats.unitsCount.toLocaleString()}
          icon={Truck}
          trend="+12"
          trendUp={true}
          loading={stats.loading}
        />
        <StatCard
          title="System Status"
          value={stats.systemStatus}
          icon={Activity}
          trend="Healthy"
          trendUp={true}
          loading={stats.loading}
        />
      </div>

      {/* Main Content Grid - Flex grow to fill remaining space */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
        {/* Left Column (2/3) - Recent Activity */}
        <div className="lg:col-span-2 flex flex-col min-h-0">
          <div className="bg-card border border-border rounded-xl p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Recent Activity
              </h2>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => navigate(is_superadmin ? '/settings/global-audit' : '/settings/audit')}>View All</Button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4 pr-2 min-h-0">
              {data.loading ? (
                <div className="space-y-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="flex gap-4 animate-pulse">
                      <div className="w-10 h-10 bg-muted rounded-full flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : data.logs.length > 0 ? (
                data.logs.slice(0, 10).map((log) => {
                  // Determine badge color based on action type
                  // IMPORTANT: Check negative conditions first (failed, rejected) before positive ones (login, success)
                  const getActionStyle = (action: string) => {
                    const a = action.toLowerCase();
                    // Check failures/errors first (red) - must come before login/success checks
                    if (a.includes('failed') || a.includes('rejected') || a.includes('deleted') || a.includes('removed') || a.includes('suspicious')) {
                      return 'bg-red-500/20 text-red-400 border-red-500/30';
                    }
                    // Success states (green)
                    if (a.includes('login') || a.includes('success') || a.includes('approved') || a.includes('activated')) {
                      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
                    }
                    // Creation states (blue)
                    if (a.includes('created') || a.includes('added') || a.includes('invited') || a.includes('create')) {
                      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
                    }
                    // Update/modification states (amber)
                    if (a.includes('updated') || a.includes('changed') || a.includes('modified') || a.includes('update')) {
                      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
                    }
                    return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
                  };

                  // Parse "via X" from details
                  const parseDetails = (details: string) => {
                    const viaMatch = details.match(/via\s+(.+?)(?:\s+login)?$/i);
                    const remaining = details.replace(/via\s+.+$/i, '').trim();
                    return {
                      via: viaMatch ? viaMatch[1].replace(/\s+login$/i, '') : null,
                      rest: remaining || null
                    };
                  };

                  const parsed = log.details ? parseDetails(log.details) : { via: null, rest: null };

                  // Format role for display (e.g., "group_admin" → "Group Admin")
                  const formatRole = (role: string): string => {
                    if (!role || role === '-') return '';
                    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                  };

                  const formattedRole = formatRole(log.role);

                  return (
                    <div
                      key={log.id}
                      className="flex gap-3 cursor-pointer hover:bg-muted/50 p-2.5 rounded-lg transition-colors"
                      onClick={() => navigate(is_superadmin ? '/settings/global-audit' : '/settings/audit')}
                    >
                      <div className="flex-shrink-0 pt-0.5">
                        <Avatar className="w-7 h-7 border border-border">
                          <AvatarFallback className="bg-muted text-muted-foreground text-[9px]">
                            {log.name ? log.name.split(' ').map(n => n[0]).join('') : log.user.split('@')[0].substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div className="min-w-0 flex-1 space-y-1">
                        {/* Line 1: Action Badge + User + Role Badge + Method Badge */}
                        <div className="flex flex-wrap items-center gap-1.5 text-xs">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${getActionStyle(log.action)}`}>
                            {formatAction(log.action)}
                          </span>
                          <span className="font-medium text-foreground">{log.name || log.user.split('@')[0]}</span>
                          {/* Show role badge OR parsed via, not both - prefer role */}
                          {formattedRole ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-300 text-[10px]">
                              via {formattedRole}
                            </span>
                          ) : parsed.via ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-300 text-[10px]">
                              via {parsed.via}
                            </span>
                          ) : null}
                        </div>
                        {/* Line 2: Metadata - Time, Company, IP, Location */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground/70">
                          <span>{formatAuditTimestamp(log.timestamp)}</span>
                          {log.company && (
                            <>
                              <span>•</span>
                              <span className="inline-flex items-center px-1 py-0 rounded bg-[#8FB560]/15 text-[#8FB560] text-[9px] font-medium">
                                {log.company}
                              </span>
                            </>
                          )}
                          {log.ip && (
                            <>
                              <span>•</span>
                              <span className="font-mono text-[9px]">{log.ip === '::1' || log.ip === '127.0.0.1' ? 'localhost' : log.ip}</span>
                            </>
                          )}
                          {log.location && (
                            <>
                              <span>•</span>
                              <span className="inline-flex items-center gap-0.5">
                                <MapPin className="w-2 h-2" />
                                {log.location}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">No recent activity</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Column (1/3) - User Info + Units by Model stacked */}
        <div className="lg:col-span-1 flex flex-col gap-4 min-h-0">
          {/* User Info Card - Compact, shrink to fit */}
          <div className="bg-card border border-border rounded-xl p-4 flex-shrink-0">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-muted rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-muted rounded w-20" />
                    <div className="h-2.5 bg-muted rounded w-28" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <div className="h-3 bg-muted rounded w-10" />
                    <div className="h-3 bg-muted rounded w-14" />
                  </div>
                  <div className="flex justify-between">
                    <div className="h-3 bg-muted rounded w-14" />
                    <div className="h-3 bg-muted rounded w-20" />
                  </div>
                </div>
                <div className="h-8 bg-muted rounded w-full mt-3" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-[#8FB560] text-foreground text-sm">
                      {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground text-sm truncate">
                      {user ? [user.first_name, user.last_name].filter(Boolean).join(' ') || 'User' : 'User'}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="space-y-1.5 mb-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Role</span>
                    <span className="text-foreground font-medium capitalize">
                      {is_superadmin ? 'Superadmin' : 'Admin'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Company</span>
                    <span className="text-foreground font-medium truncate ml-2">
                      {companies[0]?.name || '-'}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="w-full bg-muted hover:bg-muted/80 text-foreground"
                  onClick={() => navigate('/settings/profile')}
                >
                  Edit Profile
                </Button>
              </>
            )}
          </div>

          {/* Units by Model - Vertical card layout */}
          <div className="bg-card border border-border rounded-xl p-4 flex-1 flex flex-col min-h-0 overflow-hidden">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3 flex-shrink-0">
              <Truck className="w-4 h-4 text-muted-foreground" />
              Units by Model
            </h2>
            {stats.loading ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-2 animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="h-3 bg-muted rounded w-20" />
                      <div className="h-3 bg-muted rounded w-10" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-1.5" />
                      <div className="h-3 bg-muted rounded w-8" />
                    </div>
                  </div>
                ))}
              </div>
            ) : modelBreakdown.length > 0 ? (
              <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                {modelBreakdown.map((item) => (
                  <div key={item.model} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground font-medium">{item.model}</span>
                      <span className="text-muted-foreground">{item.percentage}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-[#8FB560] h-1.5 rounded-full transition-all duration-500"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                      <span className="text-muted-foreground text-xs w-8 text-right">
                        {item.count.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No units found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}